import api from './api';

export interface CommunityChannel {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  professional_type: string;
  sort_order: number;
  member_count: number;
  is_member: boolean;
  recent_post: { title: string; created_at: string } | null;
}

export interface CommunityPost {
  id: string;
  author_id: string;
  title: string;
  content?: string;
  excerpt?: string;
  featured_image?: string;
  type: 'article' | 'question' | 'discussion' | 'announcement';
  likes_count: number;
  comments_count: number;
  views_count: number;
  is_pinned: boolean;
  created_at: string;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
  category_id: string;
  category_name: string;
  category_slug: string;
  category_color: string;
  category_professional_type?: string;
  comments?: CommunityComment[];
  user_liked?: boolean;
}

export interface CommunityComment {
  id: string;
  parent_id?: string | null;
  content: string;
  likes_count: number;
  created_at: string;
  author_id: string;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
  replies?: CommunityComment[];
}

export const communityApi = {
  getChannels: async (): Promise<CommunityChannel[]> => {
    const res = await api.get('/community/channels');
    return res.data.data;
  },

  joinChannel: async (channelId: string) => {
    const res = await api.post(`/community/channels/${channelId}/join`);
    return res.data;
  },

  leaveChannel: async (channelId: string) => {
    const res = await api.post(`/community/channels/${channelId}/leave`);
    return res.data;
  },

  getPosts: async (params: {
    category_id?: string;
    search?: string;
    my_channels?: boolean;
    page?: number;
    limit?: number;
  }): Promise<CommunityPost[]> => {
    const res = await api.get('/community/forum', { params });
    return res.data.data;
  },

  getPost: async (id: string): Promise<CommunityPost> => {
    const res = await api.get(`/community/forum/${id}`);
    return res.data.data;
  },

  createPost: async (data: {
    title: string;
    content: string;
    category_id: string;
    type?: string;
    featured_image?: string;
  }): Promise<CommunityPost> => {
    const res = await api.post('/community/forum', data);
    return res.data.data;
  },

  addComment: async (postId: string, content: string, parent_id?: string): Promise<CommunityComment> => {
    const res = await api.post(`/community/forum/${postId}/comments`, { content, parent_id });
    return res.data.data;
  },

  toggleLike: async (postId: string): Promise<{ liked: boolean }> => {
    const res = await api.post(`/community/forum/${postId}/like`);
    return res.data;
  },

  updatePost: async (
    id: string,
    data: { title: string; content: string; type?: string; featured_image?: string }
  ): Promise<CommunityPost> => {
    const res = await api.put(`/community/forum/${id}`, data);
    return res.data.data;
  },

  uploadImage: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await api.post('/community/upload-image', formData);
    return res.data.url;
  },

  deletePost: async (id: string): Promise<void> => {
    await api.delete(`/community/forum/${id}`);
  },

  deleteComment: async (postId: string, commentId: string): Promise<void> => {
    await api.delete(`/community/forum/${postId}/comments/${commentId}`);
  },
};
